import { computed, defineComponent, toRefs, ref } from "vue";
import KaiyakuEntryCheckTable from "./components/KaiyakuEntryCheckTable.vue";
import KaiyakuEntryHasonTable from "./components/KaiyakuEntryHasonTable.vue";
import { store } from "../store/TaikyoStore";
import { Log } from "~/extensions/utils";
import { useRouter } from "vue-router";
import { MsgBox } from "~/components/MsgBox";
import Loading from 'quasar/src/plugins/loading/Loading.js';
import { myPageStore } from "~/store/MyPageStore";
import { NyukyoStatus, PageID, RoomSb, HanyoKb } from "~/extensions/utils/DefineTypes";
import { isNullOrUndefined } from "~/extensions/utils/DefineFunction";
import { commonStore } from "~/store/CommonStore";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { GetHanyoParam } from "src/api/Common/Commands";
import { HanyoHelper } from "src/extensions/utils/HanyoHelper";
const log = Log.create("KaiyakuEntryCheckPage");
export default defineComponent({
    name: "KaiyakuEntryCheckPage",
    components: {
        BreadcrumbList,
        KaiyakuEntryCheckTable,
        KaiyakuEntryHasonTable
    },
    beforeRouteEnter(to, from, next) {
        const fromScreen = from.name;
        const relatedScreens = ["kaiyaku-entry", "kaiyaku-entry-hason"];
        if (fromScreen === "kaiyaku-guide" && store.taikyoEntity.status !== 2) {
            next();
            return;
        }
        if (relatedScreens.includes(fromScreen)) {
            next();
            return;
        }
        next({ name: "taikyo" });
    },
    async setup() {
        const router = useRouter();
        // ページ情報
        const pageName = commonStore.pageName(PageID.kaiyakuEntryCheck);
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        const isShinsei = store.taikyoEntity.status === NyukyoStatus.Approval;
        // ストアデータ
        const taikyoEntity = toRefs(store).taikyoEntity;
        const kaiyakuForInput = toRefs(store).kaiyakuForInput;
        // UI状態
        const tab = ref("entry");
        const showHasonTab = computed(() => !store.taikyoEntity.needTachiai && myPageStore.selectedNyukyokeiyaku.roomSb === RoomSb.Room);
        // ダイアログ状態
        const isKouzaChangeDialog = ref(false);
        // 口座種別情報
        const kouzaForInput = ref([]);
        // 口座情報チェック
        const isKouzaUnResgistered = computed(() => myPageStore.personal.bankCd === 0 &&
            myPageStore.personal.bansCd === 0 &&
            myPageStore.personal.kouzaSb === 0 &&
            myPageStore.personal.kouzaNo === 0 &&
            (myPageStore.personal.kouzaName === "" || isNullOrUndefined(myPageStore.personal.kouzaName)) &&
            (myPageStore.personal.kouzaKana === "" || isNullOrUndefined(myPageStore.personal.kouzaKana)));
        /**
         * 口座情報が変更されたかどうかを判定
         */
        const isKouzaChanged = computed(() => myPageStore.personal.bankCd !== store.taikyoEntity.henkinBankCd ||
            myPageStore.personal.bankName !== store.taikyoEntity.henkinBankName ||
            myPageStore.personal.bansCd !== store.taikyoEntity.henkinBansCd ||
            myPageStore.personal.bansName !== store.taikyoEntity.henkinBansName ||
            myPageStore.personal.kouzaSb !== store.taikyoEntity.henkinKouzaSb ||
            myPageStore.personal.kouzaNo !== Number(store.taikyoEntity.henkinKouzaNo) ||
            myPageStore.personal.kouzaName !== store.taikyoEntity.henkinKouzaName ||
            myPageStore.personal.kouzaKana !== store.taikyoEntity.henkinKouzaKana);
        const initView = async () => {
            try {
                let gethanyoParam = [new GetHanyoParam(HanyoKb.KouzaSb)];
                let hanyoList = await HanyoHelper.getHanyoList(gethanyoParam);
                const publishKbnList = hanyoList.GetByHanyoKb(HanyoKb.KouzaSb);
                kouzaForInput.value = publishKbnList
                    .filter(x => [1, 2].includes(x.value))
                    .map(x => ({
                    ...x,
                    label: x.option1
                }));
            }
            catch (ex) {
                log.error("initView", ex);
                await MsgBox.error(ex?.data?.errorMessage ?? ex?.message ?? "エラーが発生しました。");
            }
        };
        /**
         * 申請ボタン押下時の処理
         */
        const onApplication = () => {
            // 単一契約の場合
            if (!myPageStore.isMultipleKeiyaku) {
                apply(true);
                return;
            }
            // 複数契約で口座未登録の場合
            if (isKouzaUnResgistered.value) {
                apply(true);
                return;
            }
            // 口座情報が変更されている場合は確認ダイアログを表示
            if (isKouzaChanged.value) {
                isKouzaChangeDialog.value = true;
                return;
            }
            // それ以外の場合は申請処理を実行
            apply(true);
        };
        /**
         * 申請処理
         */
        const apply = async (isKouzaChange) => {
            // ストアのクリア前に破損確認フラグを保持
            const isHasonCheck = store.kaiyakuForInput.keyReturnNameKb !== NyukyoStatus.Request && myPageStore.selectedNyukyokeiyaku.roomSb === RoomSb.Room;
            // ダイアログを非表示
            isKouzaChangeDialog.value = false;
            try {
                Loading.show();
                await store.update(isKouzaChange);
                await router.push({
                    name: "kaiyaku-entry-complete",
                    query: { isHasonCheck: isHasonCheck ? "true" : "false" }
                });
                store.clear();
                myPageStore.load();
                await myPageStore.waitForBasic();
            }
            catch (ex) {
                log.error("apply", ex);
                if (ex.data?.code === "not_found") {
                    MsgBox.error(ex?.data?.errorMessage ?? ex?.message ?? "エラーが発生しました。");
                }
                else {
                    MsgBox.error(ex?.message ?? "エラーが発生しました。");
                    if (ex.data !== null && ex.data.code === "error.bank") {
                        await router.push({
                            name: "kaiyaku-entry-complete",
                            query: { isHasonCheck: isHasonCheck ? "true" : "false" }
                        });
                        try {
                            store.clear();
                            myPageStore.load();
                            await myPageStore.waitForBasic();
                        }
                        catch (ex) {
                            MsgBox.error(ex?.data?.errorMessage ?? ex?.message ?? "エラーが発生しました。");
                        }
                    }
                }
            }
            finally {
                Loading.hide();
            }
        };
        await initView();
        return {
            // ページ情報
            pageName,
            PageID,
            router,
            isShinsei,
            isMultipleKeiyaku,
            // ストアデータ
            taikyoEntity,
            kaiyakuForInput,
            // UI状態
            tab,
            showHasonTab,
            // ダイアログ
            isKouzaChangeDialog,
            // 口座種別情報
            kouzaForInput,
            // ハンドラー
            onApplication,
            apply,
            // ストア参照
            myPageStore,
            RoomSb,
            commonStore
        };
    }
});
